/**
 * AI Recruit - Bulk Upload Resumes (Candidates page)
 *
 * Job-less sibling of BulkApplyResumesModal (job detail page). Same UX and
 * interaction model, adapted for plain candidate-pool uploads:
 *
 *   files → duplicate check (3 at a time)
 *         ├─ no duplicate  → uploaded straight away, never waits for a human
 *         └─ duplicate      → parked as "Needs your decision"; the rest of the
 *                             batch KEEPS FLOWING. The decision is taken in the
 *                             exact same DuplicateDetectionModal single upload
 *                             (UploadModal) uses (Cancel / Upload Anyway).
 *
 * While the dialog is open, a pending decision opens that popup immediately —
 * so it feels like single upload. While MINIMIZED, it does not steal focus:
 * the tray turns amber, shows "N need your decision", and an actionable toast
 * offers to reopen.
 *
 * The single-resume "Upload Resumes" flow (UploadModal.tsx) on this page is
 * completely untouched — this reuses the same POST /resumes/upload endpoint
 * per file, uploaded concurrently, with its own batch status-polling endpoint.
 */

import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'react-hot-toast'
import {
  CloudArrowUpIcon,
  XMarkIcon,
  DocumentTextIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  ArrowPathIcon,
  ArrowTopRightOnSquareIcon,
  TrashIcon,
  ClockIcon,
  MinusIcon,
  ArrowsPointingOutIcon,
  InformationCircleIcon,
} from '@heroicons/react/24/outline'

import { resumesApi } from '@/services/api'
import { fetchSources } from '@/services/sourceApi'
import LoadingSpinner from '@/components/common/LoadingSpinner'
import SourceSelector from '@/components/common/SourceSelector'
import DuplicateDetectionModal, { type DuplicateDetectionData } from './DuplicateDetectionModal'

interface BulkUploadResumesModalProps {
  isOpen: boolean
  onClose: () => void
  /** Called once the batch has finished so the caller can refresh its results. */
  onCompleted?: () => void
}

interface BulkUploadConfig {
  max_files: number
  max_file_size_mb: number
  allowed_file_types: string[]
}

// Used only until GET /resumes/bulk-upload-config responds (and if it fails).
// Kept in sync with core/config.py's own defaults so this brief loading-state
// render shows the real limits instead of flashing a stale number.
const FALLBACK_CONFIG: BulkUploadConfig = {
  max_files: 15,
  max_file_size_mb: 50,
  allowed_file_types: ['pdf', 'doc', 'docx', 'txt'],
}

// How many files are checked/uploaded at once. Duplicate detection runs a Claude
// contact-extraction call per file, so this balances a fast batch against
// stampeding the LLM with parallel calls.
const WORK_CONCURRENCY = 5

/** A staged file plus its own source. Empty sourceId means "inherit the main source". */
interface FileEntry {
  key: string
  file: File
  sourceId: string
}

type RowPhase =
  | 'queued'
  | 'checking'
  | 'needs_decision'
  | 'uploading'
  | 'processing'
  | 'analyzed'
  | 'failed'
  | 'skipped'
  | 'cancelled'

interface FileRow {
  key: string
  file: File
  sourceId: string
  phase: RowPhase
  duplicateData?: DuplicateDetectionData
  duplicateCount?: number
  wasDuplicate?: boolean
  resumeId?: string
  candidate?: string
  email?: string
  phone?: string
  experience?: number
  isDuplicate?: boolean
  duplicateConfidence?: number
  failureReason?: string
}

type FilterKey = 'all' | 'processing' | 'decision' | 'analyzed' | 'failed' | 'skipped'

const formatSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

const PHASE_PILL: Record<RowPhase, { label: string; cls: string; spin?: boolean; icon?: React.ReactNode }> = {
  queued: { label: 'Queued', cls: 'bg-gray-50 text-gray-500 border-gray-200', icon: <ClockIcon className="h-4 w-4" /> },
  checking: { label: 'Checking duplicate', cls: 'bg-blue-50 text-blue-700 border-blue-200', spin: true },
  needs_decision: { label: 'Needs your decision', cls: 'bg-amber-50 text-amber-800 border-amber-300', icon: <ExclamationTriangleIcon className="h-4 w-4" /> },
  uploading: { label: 'Uploading', cls: 'bg-blue-50 text-blue-700 border-blue-200', spin: true },
  // This popup's job for a row ends the moment it's inserted into the
  // database -- it does NOT track AI analysis from here on (see the Candidate
  // list's own Queued/Processing/Analyzed badges, and the notification bell's
  // history panel, for that). So 'processing' is a TERMINAL state for this
  // popup's display purposes, not a live "still working" one -- no spinner,
  // a done-looking checkmark instead.
  processing: { label: 'Uploaded', cls: 'bg-green-50 text-green-700 border-green-200', icon: <CheckCircleIcon className="h-4 w-4" /> },
  analyzed: { label: 'Analyzed', cls: 'bg-green-50 text-green-700 border-green-200', icon: <CheckCircleIcon className="h-4 w-4" /> },
  failed: { label: 'Failed', cls: 'bg-red-50 text-red-700 border-red-200', icon: <ExclamationTriangleIcon className="h-4 w-4" /> },
  skipped: { label: 'Skipped', cls: 'bg-gray-100 text-gray-500 border-gray-200', icon: <XMarkIcon className="h-4 w-4" /> },
  cancelled: { label: 'Cancelled', cls: 'bg-gray-100 text-gray-500 border-gray-200', icon: <XMarkIcon className="h-4 w-4" /> },
}

// Genuinely still-in-flight from THIS popup's perspective -- 'processing'
// deliberately excluded (see PHASE_PILL.processing above).
const IN_FLIGHT_PHASES: RowPhase[] = ['queued', 'checking', 'uploading']

const PhasePill: React.FC<{ phase: RowPhase }> = ({ phase }) => {
  const { label, cls, spin, icon } = PHASE_PILL[phase]
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-xs font-medium whitespace-nowrap ${cls}`}>
      {spin ? <LoadingSpinner size="sm" /> : icon}
      {label}
    </span>
  )
}

const BulkUploadResumesModal: React.FC<BulkUploadResumesModalProps> = ({
  isOpen,
  onClose,
  onCompleted,
}) => {
  const [source_id, setSourceId] = useState<string>('')
  const [useSameSourceForAll, setUseSameSourceForAll] = useState(true)
  const [entries, setEntries] = useState<FileEntry[]>([])
  const [dragOver, setDragOver] = useState(false)
  const [started, setStarted] = useState(false)
  const [rows, setRows] = useState<FileRow[]>([])
  const [trackedIds, setTrackedIds] = useState<string[]>([])
  const [filter, setFilter] = useState<FilterKey>('all')
  const [minimized, setMinimized] = useState(false)
  const [activeDecisionKey, setActiveDecisionKey] = useState<string | null>(null)

  const runIdRef = useRef(0)
  const stoppedRef = useRef(false)
  const notifiedRef = useRef(false)
  const onCompletedRef = useRef(onCompleted)
  useEffect(() => { onCompletedRef.current = onCompleted }, [onCompleted])
  const alertedKeysRef = useRef<Set<string>>(new Set())

  const queryClient = useQueryClient()

  const configQuery = useQuery({
    queryKey: ['bulk-upload-config'],
    queryFn: async () => (await resumesApi.getBulkUploadConfig()).data as BulkUploadConfig,
    enabled: isOpen,
    staleTime: 5 * 60 * 1000,
  })
  const config = configQuery.data ?? FALLBACK_CONFIG
  const maxFiles = config.max_files
  const maxBytes = config.max_file_size_mb * 1024 * 1024
  const allowedTypes = config.allowed_file_types
  const acceptAttr = allowedTypes.map((t) => `.${t}`).join(',')

  const sourcesQuery = useQuery({
    queryKey: ['sources', 'resume'],
    queryFn: () => fetchSources('resume'),
    enabled: isOpen,
    staleTime: 5 * 60 * 1000,
  })
  const sources = useMemo(
    () => (sourcesQuery.data ? [...sourcesQuery.data].sort((a, b) => a.name.localeCompare(b.name)) : []),
    [sourcesQuery.data]
  )
  const sourceName = useCallback(
    (id?: string) => sources.find((s) => s.id === id)?.name || '—',
    [sources]
  )

  const resetState = useCallback(() => {
    setSourceId('')
    setUseSameSourceForAll(true)
    setEntries([])
    setDragOver(false)
    setStarted(false)
    setRows([])
    setTrackedIds([])
    setFilter('all')
    setMinimized(false)
    setActiveDecisionKey(null)
    notifiedRef.current = false
    stoppedRef.current = false
    alertedKeysRef.current = new Set()
  }, [])

  useEffect(() => {
    if (isOpen) {
      resetState()
      runIdRef.current++
    }
  }, [isOpen, resetState])

  useEffect(() => {
    if (!isOpen) runIdRef.current++
  }, [isOpen])

  const updateRow = useCallback((key: string, patch: Partial<FileRow>) => {
    setRows((prev) => prev.map((r) => (r.key === key ? { ...r, ...patch } : r)))
  }, [])

  // ---- File selection ----
  const addFiles = useCallback((incoming: FileList | File[]) => {
    const arr = Array.from(incoming)
    setEntries((prev) => {
      const seen = new Set(prev.map((e) => e.key))
      const next = [...prev]
      let skippedType = 0
      let skippedBig = 0
      for (const f of arr) {
        const ext = f.name.split('.').pop()?.toLowerCase()
        if (!ext || !allowedTypes.includes(ext)) { skippedType++; continue }
        if (f.size > maxBytes) { skippedBig++; continue }
        const key = `${f.name}:${f.size}`
        if (seen.has(key)) continue
        seen.add(key)
        next.push({ key, file: f, sourceId: '' })
      }
      if (skippedType) toast.error(`${skippedType} file${skippedType === 1 ? '' : 's'} skipped — unsupported type`)
      if (skippedBig) toast.error(`${skippedBig} file${skippedBig === 1 ? '' : 's'} skipped — over ${config.max_file_size_mb}MB`)
      if (next.length > maxFiles) {
        toast.error(`You can upload up to ${maxFiles} resumes at a time — only the first ${maxFiles} were kept`)
        return next.slice(0, maxFiles)
      }
      return next
    })
  }, [allowedTypes, maxBytes, maxFiles, config.max_file_size_mb])

  const removeFile = useCallback(
    (key: string) => setEntries((prev) => prev.filter((e) => e.key !== key)),
    []
  )
  const setEntrySource = useCallback((key: string, sourceId: string) => {
    setEntries((prev) => prev.map((e) => (e.key === key ? { ...e, sourceId } : e)))
  }, [])
  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault(); setDragOver(false)
    if (e.dataTransfer.files?.length) addFiles(e.dataTransfer.files)
  }, [addFiles])
  const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files?.length) addFiles(e.target.files)
    e.target.value = ''
  }, [addFiles])

  // Build the EXISTING modal's data shape — identical to how single upload
  // (UploadModal) builds it, so the popup is visually indistinguishable.
  const buildDuplicateData = useCallback((file: File, data: any): DuplicateDetectionData => ({
    has_duplicates: true,
    extracted_info: {
      candidate_name: data.search_criteria?.name || null,
      candidate_email: data.search_criteria?.email || null,
      candidate_phone: data.search_criteria?.phone || null,
      file_name: file.name,
      file_size: file.size,
    },
    duplicate_candidates: data.results || [],
    total_duplicates_found: data.total_found || 0,
    popup_config: {
      title: 'Potential Duplicate Resumes Found',
      message: `Found ${data.total_found} potential duplicate(s) for this candidate.`,
      show_upload_button: true,
      upload_endpoint: '/api/v2/resumes/upload',
      cancel_text: 'Cancel Upload',
      upload_text: 'Upload Anyway',
    },
  }), [])

  // Ingest one file through the SAME endpoint single upload uses.
  const uploadRow = useCallback(async (row: FileRow, runId: number) => {
    if (runIdRef.current !== runId) return
    updateRow(row.key, { phase: 'uploading', failureReason: undefined })
    try {
      const fd = new FormData()
      fd.append('file', row.file)
      if (row.sourceId) fd.append('source_id', row.sourceId)
      const res = await resumesApi.upload(fd)
      if (runIdRef.current !== runId) return
      const data = res.data as any
      const id = data.id as string
      updateRow(row.key, {
        phase: 'processing',
        resumeId: id,
        isDuplicate: !!data.is_duplicate,
        duplicateConfidence: data.duplicate_confidence ?? undefined,
        candidate: data.extracted_name || row.candidate,
        // Available immediately from the pre-upload Claude contact-check --
        // unlike experience, which only exists after full AI analysis (this
        // popup no longer waits for that, so Exp stays "—" here by design).
        email: data.extracted_email || row.email,
        phone: data.extracted_phone || row.phone,
      })
      setTrackedIds((prev) => (prev.includes(id) ? prev : [...prev, id]))
      // Refresh the candidates list AND the notification bell right away so
      // this row shows up as Queued/Processing in both places immediately --
      // don't wait for the later "everything's done analyzing" invalidate
      // below, which can be minutes away and would otherwise leave a freshly
      // uploaded resume invisible/stale in both places until it finishes.
      queryClient.invalidateQueries({ queryKey: ['resumes'] })
      queryClient.invalidateQueries({ queryKey: ['my-upload-history'] })
    } catch (e: any) {
      if (runIdRef.current !== runId) return
      updateRow(row.key, { phase: 'failed', failureReason: e.response?.data?.detail || 'Upload failed' })
      // The backend already logged this rejection for the notification
      // history (see utils/upload_failure_log.py) -- refresh it so the
      // failure shows up there right away instead of only after a reload.
      queryClient.invalidateQueries({ queryKey: ['my-upload-history'] })
    }
  }, [updateRow, queryClient])

  /**
   * Check one file, then either upload it or park it for a human decision.
   * Does NOT wait for the decision — the pool moves straight on to the next
   * file, so one duplicate never holds up the whole batch.
   */
  const processOne = useCallback(async (row: FileRow, runId: number) => {
    if (runIdRef.current !== runId) return
    updateRow(row.key, { phase: 'checking', failureReason: undefined })

    let data: any = { total_found: 0 }
    try {
      const fd = new FormData()
      fd.append('file', row.file)
      data = (await resumesApi.searchDuplicates(fd)).data
    } catch {
      data = { total_found: 0 } // dup-check failure → proceed, mirroring single upload
    }
    if (runIdRef.current !== runId) return

    if (data.total_found > 0) {
      updateRow(row.key, {
        phase: 'needs_decision',
        wasDuplicate: true,
        duplicateData: buildDuplicateData(row.file, data),
        duplicateCount: data.total_found,
      })
      return
    }
    await uploadRow(row, runId)
  }, [updateRow, buildDuplicateData, uploadRow])

  const runBatch = useCallback(async (list: FileRow[], runId: number) => {
    let cursor = 0
    const worker = async () => {
      while (cursor < list.length) {
        if (stoppedRef.current || runIdRef.current !== runId) return
        const row = list[cursor++]
        await processOne(row, runId)
      }
    }
    await Promise.all(Array.from({ length: Math.min(WORK_CONCURRENCY, list.length) }, worker))
  }, [processOne])

  const resolvedSourceFor = useCallback(
    (entry: FileEntry) => (useSameSourceForAll ? source_id : entry.sourceId || source_id),
    [useSameSourceForAll, source_id]
  )

  const entriesMissingSource = useMemo(
    () => entries.filter((e) => !resolvedSourceFor(e)),
    [entries, resolvedSourceFor]
  )

  const startBatch = useCallback(() => {
    if (!entries.length) { toast.error('Please add at least one resume'); return }
    if (useSameSourceForAll && !source_id) {
      toast.error('Please select a source first')
      return
    }
    if (entriesMissingSource.length) {
      toast.error(
        `${entriesMissingSource.length} resume(s) still need a source — set one per resume, or tick "Use this source for all resume(s)".`
      )
      return
    }
    const myRun = ++runIdRef.current
    stoppedRef.current = false
    alertedKeysRef.current = new Set()
    const initial: FileRow[] = entries.map((e, i) => ({
      key: `${e.file.name}-${e.file.size}-${i}`,
      file: e.file,
      sourceId: resolvedSourceFor(e),
      phase: 'queued',
    }))
    notifiedRef.current = false
    setRows(initial)
    setStarted(true)
    void runBatch(initial, myRun)
  }, [entries, useSameSourceForAll, source_id, entriesMissingSource, runBatch, resolvedSourceFor])

  // ---- Duplicate decisions ----
  const pendingDecisions = useMemo(() => rows.filter((r) => r.phase === 'needs_decision'), [rows])
  const activeDecisionRow = useMemo(
    () => rows.find((r) => r.key === activeDecisionKey && r.phase === 'needs_decision') || null,
    [rows, activeDecisionKey]
  )

  useEffect(() => {
    if (!started || minimized || activeDecisionKey) return
    const next = pendingDecisions[0]
    if (next) setActiveDecisionKey(next.key)
  }, [started, minimized, activeDecisionKey, pendingDecisions])

  useEffect(() => {
    if (!minimized) return
    for (const row of pendingDecisions) {
      if (alertedKeysRef.current.has(row.key)) continue
      alertedKeysRef.current.add(row.key)
      toast(
        (t) => (
          <div className="flex items-start gap-3">
            <div className="text-sm">
              <p className="font-medium text-gray-900">Duplicate found</p>
              <p className="text-gray-600 mt-0.5 max-w-xs truncate" title={row.file.name}>{row.file.name}</p>
            </div>
            <button
              onClick={() => { toast.dismiss(t.id); setMinimized(false) }}
              className="px-2.5 py-1 text-xs font-medium text-amber-800 bg-amber-100 border border-amber-300 rounded-md hover:bg-amber-200 whitespace-nowrap"
            >
              Review
            </button>
          </div>
        ),
        { icon: '⚠️', duration: 10000 }
      )
    }
  }, [minimized, pendingDecisions])

  const decide = useCallback((key: string, action: 'upload' | 'skip') => {
    const row = rows.find((r) => r.key === key)
    setActiveDecisionKey(null)
    if (!row) return
    if (action === 'upload') {
      void uploadRow({ ...row, duplicateData: undefined }, runIdRef.current)
    } else {
      updateRow(key, { phase: 'skipped' })
    }
  }, [rows, uploadRow, updateRow])

  // ---- Status polling ----
  const statusQuery = useQuery({
    queryKey: ['bulk-upload-status', trackedIds],
    queryFn: async () => {
      const res = await resumesApi.getBulkUploadStatus(trackedIds)
      return res.data as { processing: number; items: any[] }
    },
    enabled: isOpen && started && trackedIds.length > 0,
    refetchInterval: (query) => {
      const d = query.state.data as { processing: number } | undefined
      return d && d.processing > 0 ? 5000 : false
    },
  })

  // This popup no longer reflects AI-analysis progress on screen (see
  // PHASE_PILL.processing above) — a row that reached 'processing' stays
  // showing "Uploaded" for good, so the popup can't say "Finished" while a
  // row still visibly looks like it's mid-flight. The Candidate list's own
  // Queued/Processing/Analyzed badges, and the notification bell's history
  // panel, are where analysis progress actually gets tracked now.
  //
  // The poll itself keeps running in the background purely to know WHEN
  // analysis has finished, so other open views (the candidates list, a
  // dashboard) refresh without the user having to reload — it deliberately
  // no longer touches `rows`.
  useEffect(() => {
    const stillProcessing = (statusQuery.data?.processing ?? 0) > 0
    if (stillProcessing) {
      notifiedRef.current = false
    } else if (statusQuery.data && !notifiedRef.current) {
      notifiedRef.current = true
      queryClient.invalidateQueries({ queryKey: ['resumes'] })
      queryClient.invalidateQueries({ queryKey: ['my-upload-history'] })
      onCompletedRef.current?.()
    }
  }, [statusQuery.data, queryClient])

  // ---- Derived counts ----
  const counts = useMemo(() => {
    // `inserting` is genuinely still in-flight (checking/uploading) — this
    // popup still has work to do. `uploaded` is 'processing': successfully
    // inserted, terminal for THIS popup's display (see PHASE_PILL.processing).
    // `analyzed` stays for structural completeness but is effectively unused
    // now — nothing sets phase to 'analyzed' here anymore.
    const c = { queued: 0, inserting: 0, uploaded: 0, decision: 0, analyzed: 0, failed: 0, skipped: 0, cancelled: 0, duplicates: 0 }
    for (const r of rows) {
      if (r.phase === 'queued') c.queued++
      else if (r.phase === 'checking' || r.phase === 'uploading') c.inserting++
      else if (r.phase === 'processing') c.uploaded++
      else if (r.phase === 'needs_decision') c.decision++
      else if (r.phase === 'analyzed') c.analyzed++
      else if (r.phase === 'failed') c.failed++
      else if (r.phase === 'skipped') c.skipped++
      else if (r.phase === 'cancelled') c.cancelled++
      if (r.isDuplicate) c.duplicates++
    }
    return c
  }, [rows])

  // "Settled" and "busy" are about THIS popup's own job -- duplicate-check
  // and insert into the database -- not the AI analysis that follows. A row
  // that reached 'processing' has already been handed off to celery; this
  // popup doesn't wait for celery to finish (the candidates list tracks
  // that separately, via its own Queued/Processing/Analyzed badges), it
  // only waits until nothing is still queued, being checked/uploaded, or
  // waiting on a duplicate decision.
  const settled = rows.length - counts.queued - counts.inserting - counts.decision
  const busy = counts.queued > 0 || counts.inserting > 0 || counts.decision > 0
  const uploadsDispatched = rows.some((r) => !!r.resumeId)

  const atRiskCount = useMemo(
    () => rows.filter((r) => ['queued', 'checking', 'uploading', 'needs_decision'].includes(r.phase)).length,
    [rows]
  )

  useEffect(() => {
    if (atRiskCount === 0) return
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault()
      event.returnValue = ''
    }
    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => window.removeEventListener('beforeunload', handleBeforeUnload)
  }, [atRiskCount])

  const handleStopRemaining = useCallback(() => {
    stoppedRef.current = true
    const stopped = rows.filter((r) => r.phase === 'queued').length
    setRows((prev) => prev.map((r) => (r.phase === 'queued' ? { ...r, phase: 'cancelled' } : r)))
    toast(`Stopped ${stopped} queued resume(s). Anything already uploaded will finish.`, {
      icon: '🛑', duration: 5000,
    })
  }, [rows])

  const retryRow = useCallback((row: FileRow) => {
    void processOne(row, runIdRef.current)
  }, [processOne])

  const openCandidate = (id?: string) => { if (id) window.open(`/resumes/${id}`, '_blank', 'noopener') }

  const handleCloseAll = useCallback(() => {
    const queued = rows.filter((r) => r.phase === 'queued').length
    const awaiting = counts.decision
    // Deliberately reads statusQuery's live `processing` count, NOT
    // rows.filter(phase === 'processing') -- that phase is frozen the
    // moment a row is inserted (see PHASE_PILL.processing) and never
    // updates again on screen, so it would still count a resume that
    // finished analyzing minutes ago as "in flight" here. statusQuery
    // keeps polling in the background specifically so this check (and the
    // invalidate-on-completion effect above) can see the real state even
    // though the popup itself stopped displaying it.
    const inFlight = statusQuery.data?.processing ?? 0
    const lost = queued + awaiting

    if (lost > 0) {
      const parts: string[] = []
      if (queued > 0) parts.push(`${queued} resume(s) haven't started uploading`)
      if (awaiting > 0) parts.push(`${awaiting} are waiting for your duplicate decision`)
      const ok = window.confirm(
        `${parts.join(' and ')} — these will be cancelled.` +
        (inFlight > 0 ? `\n\n${inFlight} already uploaded will keep analyzing in the background.` : '') +
        `\n\nClose anyway?`
      )
      if (!ok) return
    } else if (inFlight > 0) {
      toast(`${inFlight} resume(s) will keep analyzing in the background.`, { icon: '⏳', duration: 4000 })
    }

    stoppedRef.current = true
    runIdRef.current++
    if (started) {
      queryClient.invalidateQueries({ queryKey: ['resumes'] })
      queryClient.invalidateQueries({ queryKey: ['my-upload-history'] })
    }
    resetState()
    onClose()
  }, [rows, counts.decision, started, resetState, onClose, queryClient, statusQuery.data])

  const filteredRows = useMemo(() => {
    if (filter === 'all') return rows
    if (filter === 'processing') return rows.filter((r) => IN_FLIGHT_PHASES.includes(r.phase))
    if (filter === 'decision') return rows.filter((r) => r.phase === 'needs_decision')
    if (filter === 'analyzed') return rows.filter((r) => r.phase === 'processing' || r.phase === 'analyzed')
    if (filter === 'failed') return rows.filter((r) => r.phase === 'failed')
    if (filter === 'skipped') return rows.filter((r) => r.phase === 'skipped' || r.phase === 'cancelled')
    return rows
  }, [rows, filter])

  const noteText = (row: FileRow): string | null => {
    if (row.phase === 'needs_decision') {
      return `${row.duplicateCount ?? 0} possible duplicate(s) — your decision needed`
    }
    if (row.phase === 'skipped' && row.wasDuplicate) return 'Duplicate — cancelled by you'
    if (row.phase === 'cancelled') return 'Cancelled before upload'
    // row.failureReason is always plain language by this point -- backend
    // sends it pre-translated (see utils/failure_messages.py), never raw
    // exception/infrastructure text.
    if (row.phase === 'failed' && row.failureReason) return row.failureReason
    if (row.wasDuplicate) return 'Duplicate — uploaded anyway'
    if (row.isDuplicate) {
      const pct = row.duplicateConfidence != null ? ` (${Math.round(row.duplicateConfidence * 100)}%)` : ''
      return `Flagged as possible duplicate${pct}`
    }
    return null
  }

  if (!isOpen) return null

  const FILTERS: { key: FilterKey; label: string; count: number }[] = [
    { key: 'all', label: 'All', count: rows.length },
    { key: 'processing', label: 'In progress', count: counts.queued + counts.inserting },
    { key: 'decision', label: 'Needs decision', count: counts.decision },
    { key: 'analyzed', label: 'Uploaded', count: counts.uploaded + counts.analyzed },
    { key: 'failed', label: 'Failed', count: counts.failed },
    { key: 'skipped', label: 'Skipped', count: counts.skipped + counts.cancelled },
  ]

  // ---- Minimized tray ----
  if (minimized) {
    const total = rows.length || 1
    const pct = Math.round((settled / total) * 100)
    const needsAttention = counts.decision > 0
    return (
      <div
        className={`fixed bottom-4 right-4 z-50 w-80 rounded-lg border bg-white shadow-2xl ${
          needsAttention ? 'border-amber-400 ring-2 ring-amber-200' : 'border-gray-200'
        }`}
      >
        <div className="flex items-center justify-between gap-2 px-3 py-2 border-b border-gray-100">
          <div className="flex items-center gap-2 min-w-0">
            {needsAttention
              ? <ExclamationTriangleIcon className="h-4 w-4 text-amber-500 flex-shrink-0" />
              : busy ? <LoadingSpinner size="sm" /> : <CheckCircleIcon className="h-4 w-4 text-green-600 flex-shrink-0" />}
            <span className="text-sm font-medium text-gray-900 truncate">Upload Resume(s)</span>
          </div>
          <div className="flex items-center gap-1 flex-shrink-0">
            <button onClick={() => setMinimized(false)} className="p-1 text-gray-400 hover:text-gray-700 rounded" title="Expand" aria-label="Expand bulk upload">
              <ArrowsPointingOutIcon className="h-4 w-4" />
            </button>
            <button onClick={handleCloseAll} className="p-1 text-gray-400 hover:text-gray-700 rounded" title="Close" aria-label="Close bulk upload">
              <XMarkIcon className="h-4 w-4" />
            </button>
          </div>
        </div>

        <div className="px-3 py-2.5">
          <div className="h-1.5 w-full rounded-full bg-gray-100 overflow-hidden">
            <div
              className={`h-full rounded-full transition-all duration-500 ${busy ? 'bg-blue-500' : 'bg-green-500'}`}
              style={{ width: `${pct}%` }}
            />
          </div>
          <p className="mt-2 text-xs text-gray-600">
            {busy ? (
              <>
                {settled} of {rows.length} complete
                {counts.inserting > 0 && ` · ${counts.inserting} in progress`}
                {counts.queued > 0 && ` · ${counts.queued} waiting`}
              </>
            ) : (
              <span className="text-green-700">
                Finished — {counts.uploaded + counts.analyzed} uploaded
                {counts.failed > 0 && `, ${counts.failed} failed`}
                {counts.skipped + counts.cancelled > 0 && `, ${counts.skipped + counts.cancelled} skipped`}
              </span>
            )}
          </p>

          {needsAttention && (
            <button
              onClick={() => setMinimized(false)}
              className="mt-2 w-full inline-flex items-center justify-center gap-1.5 rounded-md border border-amber-300 bg-amber-50 px-2 py-1.5 text-xs font-semibold text-amber-800 hover:bg-amber-100"
            >
              <ExclamationTriangleIcon className="h-4 w-4" />
              {counts.decision} need{counts.decision === 1 ? 's' : ''} your decision — review
            </button>
          )}

          {atRiskCount > 0 && (
            <p className="mt-2 flex items-start gap-1.5 text-[11px] leading-snug text-blue-800">
              <InformationCircleIcon className="h-3.5 w-3.5 flex-shrink-0 mt-px" />
              <span>Don't reload — {atRiskCount} not sent yet. Other pages are safe.</span>
            </p>
          )}
        </div>
      </div>
    )
  }

  return (
    <>
      <div className="fixed inset-0 z-50 overflow-y-auto">
        <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={handleCloseAll} />

          <div className={`inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:w-full sm:p-6 ${started ? 'sm:max-w-7xl' : 'sm:max-w-2xl'}`}>
            {/* Header */}
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-3">
                <DocumentTextIcon className="h-6 w-6 text-blue-600" />
                <h3 className="text-lg font-medium text-gray-900">Upload Resume(s)</h3>
              </div>
              <div className="flex items-center gap-1">
                {started && (
                  <button
                    onClick={() => setMinimized(true)}
                    className="text-gray-400 hover:text-gray-600 p-1"
                    title="Minimize — keep uploading while you work elsewhere"
                    aria-label="Minimize bulk upload"
                  >
                    <MinusIcon className="h-5 w-5" />
                  </button>
                )}
                <button onClick={handleCloseAll} className="text-gray-400 hover:text-gray-600 p-1" aria-label="Close bulk upload">
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>
            </div>

            {!started && (
              <>
                <p className="text-xs text-gray-600 mb-4">
                  Resumes are analyzed and added to the candidate pool. If one looks like an existing candidate
                  you'll get the same duplicate confirmation as a single upload — and the rest of the batch keeps
                  going while you decide.
                </p>

                <div className="mb-4">
                  <SourceSelector
                    type="resume"
                    value={source_id}
                    onChange={setSourceId}
                    required={useSameSourceForAll}
                    label={useSameSourceForAll ? 'Source (applies to all Resume(s))' : 'Default source'}
                  />
                  <label className="mt-2 flex items-center gap-2 text-xs text-gray-600 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={useSameSourceForAll}
                      onChange={(e) => setUseSameSourceForAll(e.target.checked)}
                      className="h-3.5 w-3.5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    Use this source for all Resume(s)
                    <span className="text-gray-400">— untick to set a different source per resume</span>
                  </label>
                </div>

                <div
                  className={`border-2 border-dashed rounded-lg transition-all duration-200 ${
                    entries.length > 0 ? 'p-4' : 'p-8'
                  } ${dragOver ? 'border-blue-400 bg-blue-50' : 'border-gray-300 hover:border-gray-400'}`}
                  onDragOver={(e) => { e.preventDefault(); setDragOver(true) }}
                  onDragLeave={(e) => { e.preventDefault(); setDragOver(false) }}
                  onDrop={handleDrop}
                >
                  <div className="text-center">
                    <CloudArrowUpIcon className={`mx-auto text-gray-400 ${entries.length > 0 ? 'h-8 w-8' : 'h-12 w-12'}`} />
                    <label htmlFor="bulk-upload-resume-input" className={`cursor-pointer block ${entries.length > 0 ? 'mt-2' : 'mt-4'}`}>
                      <span className={entries.length > 0 ? 'text-sm font-medium text-gray-900' : 'text-lg font-medium text-gray-900'}>
                        Drop resumes here or <span className="text-blue-600 hover:text-blue-500">browse files</span>
                      </span>
                      <input id="bulk-upload-resume-input" type="file" multiple className="sr-only" accept={acceptAttr} onChange={handleFileSelect} />
                    </label>
                    <p className="text-sm text-gray-500 mt-2">
                      {allowedTypes.map((t) => t.toUpperCase()).join(', ')} — up to {config.max_file_size_mb}MB each,{' '}
                      <span className="font-medium text-gray-700">Max {maxFiles} Resume(s) per batch</span>
                    </p>
                  </div>
                </div>

                {entries.length > 0 && (
                  <div className="mt-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-gray-700">
                        {entries.length} of {maxFiles} file{entries.length === 1 ? '' : 's'} ready
                      </span>
                      <button onClick={() => setEntries([])} className="text-xs text-gray-500 hover:text-red-600">Clear all</button>
                    </div>

                    <div className="flex items-center gap-2 px-3 pb-1 text-[11px] font-medium uppercase tracking-wide text-gray-400">
                      <span className="flex-1 min-w-0">Resume</span>
                      <span className="w-1/2 min-w-0">Source</span>
                      <span className="w-5 flex-shrink-0" />
                    </div>

                    <div className={`border border-gray-200 rounded-md divide-y divide-gray-100 ${entries.length > 5 ? 'max-h-52 overflow-y-auto' : ''}`}>
                      {entries.map((entry) => (
                        <div key={entry.key} className="flex items-center gap-2 px-3 py-1.5 text-sm">
                          <div className="flex items-center gap-2 min-w-0 flex-1">
                            <DocumentTextIcon className="h-4 w-4 text-gray-400 flex-shrink-0" />
                            <span className="truncate text-gray-800" title={entry.file.name}>{entry.file.name}</span>
                            <span className="text-xs text-gray-400 flex-shrink-0">{formatSize(entry.file.size)}</span>
                          </div>

                          <div className="w-1/2 min-w-0">
                            <select
                              value={useSameSourceForAll ? '' : entry.sourceId}
                              onChange={(e) => setEntrySource(entry.key, e.target.value)}
                              disabled={useSameSourceForAll || sourcesQuery.isLoading}
                              aria-label={`Source for ${entry.file.name}`}
                              className="block w-full rounded-md border-gray-300 py-1 text-xs shadow-sm focus:border-blue-500 focus:ring-blue-500 disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed"
                            >
                              <option value="">
                                {useSameSourceForAll
                                  ? source_id ? `Same as main — ${sourceName(source_id)}` : 'Same as main source'
                                  : source_id ? `Default — ${sourceName(source_id)}` : 'Select a source'}
                              </option>
                              {sources.map((s) => (
                                <option key={s.id} value={s.id}>{s.name}</option>
                              ))}
                            </select>
                          </div>

                          <button
                            onClick={() => removeFile(entry.key)}
                            className="text-gray-400 hover:text-red-600 flex-shrink-0"
                            aria-label={`Remove ${entry.file.name}`}
                          >
                            <TrashIcon className="h-4 w-4" />
                          </button>
                        </div>
                      ))}
                    </div>

                    {!useSameSourceForAll && entriesMissingSource.length > 0 && (
                      <p className="mt-2 text-xs text-amber-700">
                        {entriesMissingSource.length} resume(s) have no source yet — pick one per resume, or set a default above.
                      </p>
                    )}
                  </div>
                )}

                <div className="mt-6 flex justify-end space-x-3">
                  <button onClick={handleCloseAll} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors">Cancel</button>
                  <button
                    onClick={startBatch}
                    disabled={
                      !entries.length ||
                      (useSameSourceForAll ? !source_id : entriesMissingSource.length > 0)
                    }
                    className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    Upload Resume(s)
                  </button>
                </div>
              </>
            )}

            {started && (
              <>
                <div className="mb-3 flex items-start justify-between gap-4">
                  <div className="text-sm flex items-center gap-2">
                    {busy ? (
                      <>
                        <LoadingSpinner size="sm" />
                        <span className="text-gray-600">
                          {settled} of {rows.length} complete
                          {counts.inserting > 0 && ` · ${counts.inserting} in progress`}
                          {counts.queued > 0 && ` · ${counts.queued} waiting`}
                        </span>
                      </>
                    ) : (
                      <>
                        <CheckCircleIcon className="h-4 w-4 text-green-600" />
                        <span className="text-green-700">
                          Finished — {counts.uploaded + counts.analyzed} uploaded
                          {counts.failed > 0 && `, ${counts.failed} failed`}
                          {counts.skipped + counts.cancelled > 0 && `, ${counts.skipped + counts.cancelled} skipped`}.
                        </span>
                      </>
                    )}
                  </div>
                  {uploadsDispatched && (
                    <p className="text-xs text-gray-500 max-w-sm text-right">
                      <span className="font-medium">Minimize</span> to keep working — you'll be alerted if a
                      duplicate needs your decision.
                    </p>
                  )}
                </div>

                {!busy && counts.uploaded + counts.analyzed > 0 && (
                  <div className="mb-3 flex items-start gap-2 rounded-md border border-blue-200 bg-blue-50 px-3 py-2 text-xs text-blue-800">
                    <InformationCircleIcon className="h-4 w-4 flex-shrink-0 mt-0.5" />
                    <span>
                      <span className="font-medium">Uploaded doesn't mean analyzed yet.</span> Each resume still
                      needs to be read and scored by AI, which keeps running in the background even with this
                      window closed. Check the notification bell or the candidates list — a resume moves from
                      Queued to Processing to Analyzed there as it completes.
                    </span>
                  </div>
                )}

                {atRiskCount > 0 && (
                  <div className="mb-3 flex items-start gap-2 rounded-md border border-blue-200 bg-blue-50 px-3 py-2 text-xs text-blue-800">
                    <InformationCircleIcon className="h-4 w-4 flex-shrink-0 mt-0.5" />
                    <span>
                      <span className="font-medium">Don't reload or close the browser</span> — {atRiskCount} resume
                      {atRiskCount === 1 ? '' : 's'} {atRiskCount === 1 ? 'has' : 'have'} not reached the server yet and
                      would need re-uploading. Switching pages inside AI Recruit is safe: this panel stays with you.
                    </span>
                  </div>
                )}

                {counts.decision > 0 && (
                  <div className="mb-3 flex items-center justify-between gap-3 rounded-md border border-amber-300 bg-amber-50 px-3 py-2">
                    <div className="flex items-center gap-2 text-sm text-amber-800">
                      <ExclamationTriangleIcon className="h-4 w-4 flex-shrink-0" />
                      <span>
                        <span className="font-semibold">{counts.decision}</span> resume{counts.decision === 1 ? '' : 's'} waiting
                        on your duplicate decision. The rest of the batch is still going.
                      </span>
                    </div>
                    <button
                      onClick={() => setActiveDecisionKey(pendingDecisions[0]?.key ?? null)}
                      className="px-3 py-1.5 text-xs font-medium text-amber-800 bg-white border border-amber-300 rounded-md hover:bg-amber-100 whitespace-nowrap"
                    >
                      Review now
                    </button>
                  </div>
                )}

                <div className="flex flex-wrap gap-2 mb-3">
                  {FILTERS.map((f) => (
                    <button
                      key={f.key}
                      onClick={() => setFilter(f.key)}
                      className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-medium border transition-colors ${
                        filter === f.key
                          ? 'bg-blue-600 text-white border-blue-600'
                          : f.key === 'decision' && f.count > 0
                            ? 'bg-amber-50 text-amber-800 border-amber-300 hover:bg-amber-100'
                            : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
                      }`}
                    >
                      <span className="font-semibold">{f.count}</span> {f.label}
                    </button>
                  ))}
                </div>

                <div className="overflow-x-auto border border-gray-200 rounded-md">
                  <div className={filteredRows.length > 5 ? 'max-h-64 overflow-y-auto' : ''}>
                    <table className="min-w-full text-sm">
                      <thead className="bg-gray-50 sticky top-0">
                        <tr className="text-left text-xs font-medium text-gray-500">
                          <th className="px-3 py-2">Resume File</th>
                          <th className="px-3 py-2">Source</th>
                          <th className="px-3 py-2">Candidate</th>
                          <th className="px-3 py-2">Email</th>
                          <th className="px-3 py-2">Phone</th>
                          <th className="px-3 py-2">Exp</th>
                          <th className="px-3 py-2">Status</th>
                          <th className="px-3 py-2">Notes</th>
                          <th className="px-3 py-2 text-right">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {filteredRows.map((row) => {
                          const note = noteText(row)
                          return (
                            <tr key={row.key} className={row.phase === 'needs_decision' ? 'bg-amber-50/60' : 'hover:bg-gray-50'}>
                              <td className="px-3 py-2 max-w-[14rem]">
                                <div className="flex items-center gap-2 min-w-0">
                                  <DocumentTextIcon className="h-4 w-4 text-gray-400 flex-shrink-0" />
                                  <span className="truncate text-gray-800" title={row.file.name}>{row.file.name}</span>
                                </div>
                              </td>
                              <td className="px-3 py-2 text-gray-500 whitespace-nowrap max-w-[9rem]">
                                <span className="truncate block" title={sourceName(row.sourceId)}>{sourceName(row.sourceId)}</span>
                              </td>
                              <td className="px-3 py-2 text-gray-700 whitespace-nowrap max-w-[10rem] truncate" title={row.candidate}>{row.candidate || '—'}</td>
                              <td className="px-3 py-2 text-gray-500 max-w-[12rem]"><span className="truncate block" title={row.email}>{row.email || '—'}</span></td>
                              <td className="px-3 py-2 text-gray-500 whitespace-nowrap">{row.phone || '—'}</td>
                              <td className="px-3 py-2 text-gray-500 whitespace-nowrap">{row.experience != null ? `${row.experience} yrs` : '—'}</td>
                              <td className="px-3 py-2"><PhasePill phase={row.phase} /></td>
                              <td className="px-3 py-2 max-w-[13rem]">
                                {note ? (
                                  <span
                                    className={`text-xs truncate block ${
                                      row.phase === 'needs_decision' ? 'text-amber-800 font-medium' : 'text-amber-700'
                                    }`}
                                    title={note}
                                  >
                                    {note}
                                  </span>
                                ) : (
                                  <span className="text-gray-300 text-xs">—</span>
                                )}
                              </td>
                              <td className="px-3 py-2 text-right whitespace-nowrap">
                                {row.phase === 'needs_decision' && (
                                  <button
                                    onClick={() => setActiveDecisionKey(row.key)}
                                    className="px-2 py-1 text-xs font-semibold text-amber-800 bg-amber-100 border border-amber-300 rounded hover:bg-amber-200"
                                  >
                                    Review
                                  </button>
                                )}
                                {row.phase === 'analyzed' && row.resumeId && (
                                  <button onClick={() => openCandidate(row.resumeId)} className="text-blue-600 hover:text-blue-800 text-xs inline-flex items-center gap-1" title="View candidate in a new tab">
                                    <ArrowTopRightOnSquareIcon className="h-4 w-4" /> View
                                  </button>
                                )}
                                {row.phase === 'failed' && (
                                  <button
                                    onClick={() => retryRow(row)}
                                    className="text-gray-600 hover:text-blue-600 text-xs inline-flex items-center gap-1"
                                    title="Retry"
                                  >
                                    <ArrowPathIcon className="h-4 w-4" /> Retry
                                  </button>
                                )}
                              </td>
                            </tr>
                          )
                        })}
                        {filteredRows.length === 0 && (
                          <tr><td colSpan={9} className="px-3 py-8 text-center text-sm text-gray-400">No files in this filter.</td></tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>

                <div className="mt-6 flex items-center justify-between gap-3">
                  <div>
                    {counts.queued > 0 && (
                      <button
                        onClick={handleStopRemaining}
                        className="px-4 py-2 text-sm font-medium text-red-700 bg-white border border-red-300 rounded-md hover:bg-red-50 transition-colors"
                        title="Cancel resumes that haven't started. Anything already uploaded keeps processing."
                      >
                        Stop remaining ({counts.queued})
                      </button>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    {busy && (
                      <button
                        onClick={() => setMinimized(true)}
                        className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors inline-flex items-center gap-1.5"
                        title="Shrink to a corner tray and keep working"
                      >
                        <MinusIcon className="h-4 w-4" />
                        Minimize
                      </button>
                    )}
                    <button onClick={handleCloseAll} className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 transition-colors">
                      {busy ? 'Close' : 'Done'}
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* The EXACT duplicate popup single upload uses. Opened per duplicate;
          Cancel / Upload Anyway resolve just that one file. */}
      <DuplicateDetectionModal
        isOpen={!!activeDecisionRow}
        onClose={() => activeDecisionRow && decide(activeDecisionRow.key, 'skip')}
        onUploadAnyway={() => activeDecisionRow && decide(activeDecisionRow.key, 'upload')}
        duplicateData={activeDecisionRow?.duplicateData ?? null}
      />
    </>
  )
}

export default BulkUploadResumesModal
